import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TID } from "@/constants/testIds";
import { Loader2 } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email.trim().toLowerCase(), password);
    setLoading(false);
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden text-white flex bg-canvas">
      {/* LEFT — brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[44%] p-12 relative bg-[#E11D2E] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15 mix-blend-multiply grayscale"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1471341971476-ae15ff5dd4ea?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85')" }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <img src="/flow-logo-small.png" alt="Flow" className="h-10 w-10 object-contain rounded-md bg-white/10" />
          <div className="text-sm font-semibold tracking-wide opacity-90">Life Inside Media</div>
        </div>
        <div className="relative z-10">
          <h1 className="font-hero text-7xl xl:text-8xl leading-none">Flow.</h1>
          <p className="mt-6 opacity-90 max-w-sm leading-relaxed text-base">
            One clean workspace to assign, log and ship every deadline — for editors, shooters and managers.
          </p>
        </div>
        <div className="relative z-10 flex gap-4 text-xs font-mono opacity-75">
          <span>v2.0</span><span>·</span><span>MUMBAI</span><span>·</span><span>{new Date().getFullYear()}</span>
        </div>
      </div>

      {/* RIGHT — auth panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 relative">
        <ThemeToggle className="absolute top-5 right-5" />
        <div className="w-full max-w-sm relative z-10">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="/flow-logo-small.png" alt="Flow" className="h-11 w-11 object-contain rounded-md" />
            <div>
              <div className="font-hero text-2xl leading-none">Flow</div>
              <div className="text-xs text-white/50 mt-1">Life Inside Media</div>
            </div>
          </div>

          <div className="glass rounded-xl p-8 shadow-float">
            <h2 className="font-display text-2xl mb-1">Welcome back</h2>
            <p className="text-sm text-white/50 mb-7">Sign in to your team workspace.</p>

            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-white/80">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid={TID.loginEmail}
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-surface border-white/15 h-10 focus-visible:ring-[#E11D2E]/40"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-white/80">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid={TID.loginPassword}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-surface border-white/15 h-10 focus-visible:ring-[#E11D2E]/40"
                />
              </div>

              {error && (
                <div data-testid={TID.loginError} className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid={TID.loginSubmit}
                className="w-full h-10 bg-[#E11D2E] hover:bg-[#F03041] text-white font-semibold rounded-md transition-colors"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : "Sign in"}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-white/10 text-xs text-white/50 leading-relaxed">
              Accounts are provisioned by your manager. Contact them if you don&apos;t have access yet.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
